import { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Users, Mail, BarChart3, Clock, MousePointerClick, Eye, EyeOff, LogOut, ArrowLeft, Trash2, Edit, Plus, StickyNote, Search, Filter, ArrowRight, X, Phone, Building, Tag, Calendar, MessageSquare, Send, Activity, CheckSquare, Square, Zap } from "lucide-react";
import axios from "axios";

const API = '/api';

const SERIOUSNESS_LEVELS = [
  { value: "new_lead", label: "New Lead", color: "bg-blue-500/20 text-blue-400" },
  { value: "interested", label: "Interested", color: "bg-cyan-500/20 text-cyan-400" },
  { value: "serious_buyer", label: "Serious Buyer", color: "bg-amber-500/20 text-amber-400" },
  { value: "verified_buyer", label: "Verified Buyer", color: "bg-green-500/20 text-green-400" },
  { value: "vip_institutional", label: "VIP / Institutional", color: "bg-purple-500/20 text-purple-400" },
  { value: "not_qualified", label: "Not Qualified", color: "bg-red-500/20 text-red-400" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "follow_up", label: "Follow Up" },
  { value: "in_discussion", label: "In Discussion" },
  { value: "viewing_scheduled", label: "Viewing Scheduled" },
  { value: "closed", label: "Closed" },
  { value: "archived", label: "Archived" },
];

function formatApiError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function SeriousnessBadge({ level }) {
  const config = SERIOUSNESS_LEVELS.find(s => s.value === level) || SERIOUSNESS_LEVELS[0];
  return <span className={`text-[11px] px-2 py-0.5 ${config.color}`}>{config.label}</span>;
}

export default function AdminDashboard() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [token, setToken] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const [visitors, setVisitors] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterSeriousness, setFilterSeriousness] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [editForm, setEditForm] = useState({});
  const [referralStats, setReferralStats] = useState(null);
  const [newReferrer, setNewReferrer] = useState({ name: "", email: "", commission_rate: "", notes: "" });
  const [addReferrerOpen, setAddReferrerOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [engagementData, setEngagementData] = useState(null);
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [magicLinkSending, setMagicLinkSending] = useState(false);
  const [detailTab, setDetailTab] = useState("info");
  const [selectedInquiryIds, setSelectedInquiryIds] = useState(new Set());
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyTarget, setReplyTarget] = useState(null);
  const [replyForm, setReplyForm] = useState({ subject: "", body: "" });
  const [replySending, setReplySending] = useState(false);
  const [editInquiryOpen, setEditInquiryOpen] = useState(false);
  const [editInquiryForm, setEditInquiryForm] = useState({ name: "", email: "", message: "" });
  const [editInquiryId, setEditInquiryId] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
      setToken(res.data.token);
      setLoggedIn(true);
    } catch (err) {
      if (err.response?.data?.detail) {
        setLoginError(formatApiError(err.response.data.detail));
      } else if (err.response?.status) {
        setLoginError(`Server returned ${err.response.status}. Please try again.`);
      } else if (err.request) {
        setLoginError("Could not reach the server. Please check your connection.");
      } else {
        setLoginError(err.message || "Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => { setLoggedIn(false); setToken(""); };

  const fetchData = useCallback(async () => {
    if (!loggedIn) return;
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (filterSeriousness !== "all") params.seriousness = filterSeriousness;
      if (filterStatus !== "all") params.status = filterStatus;
      const [v, i, a, r] = await Promise.all([
        axios.get(`${API}/admin/visitors`, { headers, params, withCredentials: true }),
        axios.get(`${API}/admin/inquiries`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/analytics`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/referral-stats`, { headers, withCredentials: true }),
      ]);
      setVisitors(v.data);
      setInquiries(i.data);
      setAnalytics(a.data);
      setReferralStats(r.data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) handleLogout();
    }
  }, [loggedIn, token, searchQuery, filterSeriousness, filterStatus]);

  useEffect(() => { fetchData(); const iv = setInterval(fetchData, 30000); return () => clearInterval(iv); }, [fetchData]);

  const openVisitorDetail = async (visitor) => {
    try {
      const res = await axios.get(`${API}/admin/visitors/${visitor.id}`, { headers, withCredentials: true });
      setSelectedVisitor(res.data);
      setDetailOpen(true);
    } catch (e) {
      setSelectedVisitor(visitor);
      setDetailOpen(true);
    }
  };

  const updateVisitor = async (visitorId, updates) => {
    try {
      const res = await axios.put(`${API}/admin/visitors/${visitorId}`, updates, { headers, withCredentials: true });
      setSelectedVisitor(res.data);
      fetchData();
    } catch (e) { /* ignore */ }
  };

  const addNote = async () => {
    if (!noteText.trim() || !selectedVisitor) return;
    try {
      await axios.post(`${API}/admin/visitors/${selectedVisitor.id}/notes`, { content: noteText }, { headers, withCredentials: true });
      setNoteText("");
      const res = await axios.get(`${API}/admin/visitors/${selectedVisitor.id}`, { headers, withCredentials: true });
      setSelectedVisitor(res.data);
    } catch (e) { /* ignore */ }
  };

  const deleteNote = async (noteId) => {
    if (!selectedVisitor) return;
    try {
      await axios.delete(`${API}/admin/visitors/${selectedVisitor.id}/notes/${noteId}`, { headers, withCredentials: true });
      const res = await axios.get(`${API}/admin/visitors/${selectedVisitor.id}`, { headers, withCredentials: true });
      setSelectedVisitor(res.data);
    } catch (e) { /* ignore */ }
  };

  const deleteVisitor = async (id) => {
    if (!window.confirm("Delete this visitor permanently? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/visitors/${id}`, { headers, withCredentials: true });
      setDetailOpen(false);
      fetchData();
    } catch (e) { /* ignore */ }
  };

  const createReferrer = async () => {
    if (!newReferrer.name.trim()) return;
    try {
      await axios.post(`${API}/admin/referrers`, {
        name: newReferrer.name.trim(),
        email: newReferrer.email.trim() || null,
        commission_rate: parseFloat(newReferrer.commission_rate) || 0,
        notes: newReferrer.notes || null,
      }, { headers, withCredentials: true });
      setNewReferrer({ name: "", email: "", commission_rate: "", notes: "" });
      setAddReferrerOpen(false);
      fetchData();
    } catch (e) { /* ignore */ }
  };

  const toggleReferrer = async (id, active) => {
    try {
      await axios.put(`${API}/admin/referrers/${id}`, { active: !active }, { headers, withCredentials: true });
      fetchData();
    } catch (e) { /* ignore */ }
  };

  const deleteReferrer = async (id) => {
    if (!window.confirm("Are you sure you want to delete this referrer? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/referrers/${id}`, { headers, withCredentials: true });
      fetchData();
    } catch (e) { /* ignore */ }
  };

  const getSiteUrl = () => {
    return window.location.origin;
  };

  const openEdit = (visitor) => {
    setEditForm({ name: visitor.name || "", phone: visitor.phone || "", company: visitor.company || "", seriousness: visitor.seriousness || "new_lead", status: visitor.status || "active", tags: (visitor.tags || []).join(", ") });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!selectedVisitor) return;
    const updates = { ...editForm };
    if (updates.tags) updates.tags = updates.tags.split(",").map(t => t.trim()).filter(Boolean);
    else updates.tags = [];
    await updateVisitor(selectedVisitor.id, updates);
    setEditOpen(false);
  };

  const toggleSelect = (id, e) => {
    e?.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    if (selectedIds.size === visitors.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(visitors.map(v => v.id)));
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Permanently delete ${selectedIds.size} visitor(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await axios.post(`${API}/admin/visitors/bulk-delete`, { visitor_ids: [...selectedIds] }, { headers, withCredentials: true });
      setSelectedIds(new Set());
      fetchData();
    } catch (e) { /* ignore */ }
    finally { setBulkDeleting(false); }
  };

  const fetchEngagement = async (visitorId) => {
    setEngagementLoading(true);
    setEngagementData(null);
    try {
      const res = await axios.get(`${API}/admin/visitors/${visitorId}/engagement`, { headers, withCredentials: true });
      setEngagementData(res.data);
    } catch (e) { /* ignore */ }
    finally { setEngagementLoading(false); }
  };

  const sendMagicLink = async (visitorId) => {
    if (!window.confirm("Send a private access link to this visitor's email?")) return;
    setMagicLinkSending(true);
    try {
      const res = await axios.post(`${API}/admin/visitors/${visitorId}/send-magic-link`, {}, { headers, withCredentials: true });
      alert(`Magic link sent to ${res.data.email}. Valid for ${res.data.expires_in}.`);
    } catch (e) {
      alert("Failed to send magic link. " + (e.response?.data?.detail || ""));
    } finally { setMagicLinkSending(false); }
  };

  const openVisitorWithEngagement = async (visitor) => {
    setDetailTab("info");
    await openVisitorDetail(visitor);
    fetchEngagement(visitor.id);
  };

  const behaviorColor = (b) => {
    if (b === "deep_reading") return "text-green-400 bg-green-400/10";
    if (b === "reading") return "text-emerald-400 bg-emerald-400/10";
    if (b === "skimming") return "text-amber-400 bg-amber-400/10";
    return "text-red-400 bg-red-400/10";
  };

  const behaviorLabel = (b) => {
    if (b === "deep_reading") return "Deep Reading";
    if (b === "reading") return "Reading";
    if (b === "skimming") return "Skimming";
    return "Glanced";
  };

  const interactionIcon = (type) => {
    if (type === "document_view") return "doc";
    if (type === "gallery_view") return "img";
    if (type === "video_play") return "vid";
    return "click";
  };

  const toggleInquirySelect = (id) => {
    setSelectedInquiryIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, { headers, withCredentials: true });
      fetchData();
    } catch (e) { /* ignore */ }
  };

  const bulkDeleteInquiries = async () => {
    if (!window.confirm(`Delete ${selectedInquiryIds.size} enquiry(s) permanently?`)) return;
    try {
      await axios.post(`${API}/admin/inquiries/bulk-delete`, { visitor_ids: [...selectedInquiryIds] }, { headers, withCredentials: true });
      setSelectedInquiryIds(new Set());
      fetchData();
    } catch (e) { /* ignore */ }
  };

  const openReplyModal = (inq) => {
    setReplyTarget(inq);
    setReplyForm({ subject: `Re: Your Enquiry — The Bridgette Emerald`, body: "" });
    setReplyOpen(true);
  };

  const sendReply = async () => {
    if (!replyTarget || !replyForm.body.trim()) return;
    setReplySending(true);
    try {
      await axios.post(`${API}/admin/inquiries/${replyTarget.id}/reply`, replyForm, { headers, withCredentials: true });
      setReplyOpen(false);
      fetchData();
      alert(`Reply sent to ${replyTarget.email}`);
    } catch (e) {
      alert("Failed to send reply. " + (e.response?.data?.detail || ""));
    } finally { setReplySending(false); }
  };

  const openEditInquiry = (inq) => {
    setEditInquiryId(inq.id);
    setEditInquiryForm({ name: inq.name || "", email: inq.email || "", message: inq.message || "" });
    setEditInquiryOpen(true);
  };

  const saveEditInquiry = async () => {
    if (!editInquiryId) return;
    try {
      await axios.put(`${API}/admin/inquiries/${editInquiryId}`, editInquiryForm, { headers, withCredentials: true });
      setEditInquiryOpen(false);
      fetchData();
    } catch (e) { /* ignore */ }
  };

  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-[#111110] flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="font-serif text-3xl text-white/80 mb-2">Admin Dashboard</h1>
            <p className="text-sm text-white/30">The Bridgette Emerald — CRM & Management</p>
          </div>
          <form onSubmit={handleLogin} className="p-8 bg-emerald-card border border-white/[0.06] space-y-5" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label className="text-xs text-white/40 uppercase tracking-wider">Email</Label>
              <Input data-testid="admin-email" type="email" required value={loginForm.email} onChange={(e) => setLoginForm(p => ({ ...p, email: e.target.value }))} className="bg-transparent border-white/10 text-white/80 rounded-none h-12 text-base" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-white/40 uppercase tracking-wider">Password</Label>
              <div className="relative">
                <Input data-testid="admin-password" type={showPassword ? "text" : "password"} required value={loginForm.password} onChange={(e) => setLoginForm(p => ({ ...p, password: e.target.value }))} className="bg-transparent border-white/10 text-white/80 rounded-none h-12 text-base pr-12" />
                <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors" data-testid="toggle-password">
                  {showPassword ? <EyeOff size={18} strokeWidth={1.5} /> : <Eye size={18} strokeWidth={1.5} />}
                </button>
              </div>
            </div>
            {loginError && <p className="text-sm text-red-400/80" data-testid="admin-login-error">{loginError}</p>}
            <button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full py-5 bg-[#C5A059] hover:bg-[#E8D5A3] text-[#111110] text-xs tracking-[0.2em] uppercase font-medium h-auto disabled:opacity-50">
              {loading ? "Signing in..." : "Sign In"}
            </button>
            <a href="/" className="flex items-center justify-center gap-2 text-xs text-white/30 hover:text-gold/50 transition-colors mt-4"><ArrowLeft size={12} /> Back to Showcase</a>
          </form>
        </div>
      </div>
    );
  }

  const seriousnessBreakdown = analytics?.seriousness_breakdown || {};
  const sectionViews = analytics?.section_views || {};
  const formatSection = (s) => s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  const sortedSections = Object.entries(sectionViews).sort((a, b) => b[1] - a[1]);

  return (
    <div className="min-h-screen bg-[#111110]">
      <header className="border-b border-white/[0.06] px-4 lg:px-6 py-4 flex items-center justify-between" data-testid="admin-header">
        <div className="flex items-center gap-4">
          <a href="/" className="text-white/30 hover:text-gold/50 transition-colors"><ArrowLeft size={18} strokeWidth={1.5} /></a>
          <h1 className="font-serif text-lg text-white/70">CRM Dashboard</h1>
        </div>
        <Button onClick={handleLogout} variant="ghost" className="text-white/30 hover:text-white/60 text-xs gap-2" data-testid="admin-logout-btn"><LogOut size={14} strokeWidth={1.5} /> Logout</Button>
      </header>

      <div className="flex">
        <aside className="w-48 lg:w-56 border-r border-white/[0.06] min-h-[calc(100vh-60px)] p-3 space-y-1 hidden md:block">
          {[
            { id: "overview", icon: BarChart3, label: "Overview" },
            { id: "visitors", icon: Users, label: "Visitors CRM" },
            { id: "referrals", icon: Tag, label: "Referrals" },
            { id: "inquiries", icon: Mail, label: "Enquiries" },
            { id: "analytics", icon: Eye, label: "Analytics" },
          ].map(item => (
            <button key={item.id} data-testid={`admin-tab-${item.id}`} onClick={() => setTab(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors rounded-sm ${tab === item.id ? "bg-gold/10 text-gold" : "text-white/40 hover:text-white/60 hover:bg-white/[0.03]"}`}>
              <item.icon size={16} strokeWidth={1.5} />{item.label}
            </button>
          ))}
        </aside>

        <main className="flex-1 p-4 lg:p-6 overflow-x-hidden">
          <div className="flex md:hidden gap-2 mb-4 overflow-x-auto pb-2">
            {[
              { id: "overview", label: "Overview" },
              { id: "visitors", label: "Visitors" },
              { id: "referrals", label: "Referrals" },
              { id: "inquiries", label: "Enquiries" },
              { id: "analytics", label: "Analytics" },
            ].map(item => (
              <button key={item.id} onClick={() => setTab(item.id)} className={`px-3 py-2 text-xs uppercase tracking-wider whitespace-nowrap ${tab === item.id ? "bg-gold/10 text-gold border border-gold/20" : "text-white/40 border border-white/[0.06]"}`}>{item.label}</button>
            ))}
          </div>

          {/* OVERVIEW */}
          {tab === "overview" && (
            <div className="space-y-6">
              <h2 className="font-serif text-2xl text-white/80">Overview</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: "Site Visitors", value: analytics?.total_site_visitors || 0, icon: Users },
                  { label: "Registered", value: analytics?.total_visitors || 0, icon: Users },
                  { label: "Enquiries", value: analytics?.total_inquiries || 0, icon: Mail },
                  { label: "Events", value: analytics?.total_events || 0, icon: MousePointerClick },
                  { label: "Avg Session", value: analytics?.avg_session_duration ? `${analytics.avg_session_duration}s` : "–", icon: Clock },
                ].map((stat, i) => (
                  <div key={i} className="p-4 lg:p-5 bg-emerald-card border border-white/[0.06]" data-testid={`admin-stat-${i}`}>
                    <stat.icon size={16} strokeWidth={1.5} className="text-gold/40 mb-3" />
                    <div className="font-serif text-2xl lg:text-3xl text-white/80 mb-1">{stat.value}</div>
                    <p className="text-xs text-white/30 uppercase tracking-wider">{stat.label}</p>
                  </div>
                ))}
              </div>

              {Object.keys(seriousnessBreakdown).length > 0 && (
                <div className="p-5 bg-emerald-card border border-white/[0.06]">
                  <h3 className="text-sm text-white/50 uppercase tracking-wider mb-4">Pipeline Breakdown</h3>
                  <div className="flex flex-wrap gap-3">
                    {SERIOUSNESS_LEVELS.map(level => (
                      <div key={level.value} className="flex items-center gap-2">
                        <SeriousnessBadge level={level.value} />
                        <span className="text-sm text-white/50">{seriousnessBreakdown[level.value] || 0}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {sortedSections.length > 0 && (
                <div className="p-5 bg-emerald-card border border-white/[0.06]">
                  <h3 className="text-sm text-white/50 uppercase tracking-wider mb-4">Section Engagement</h3>
                  <div className="space-y-2">
                    {sortedSections.map(([section, count]) => {
                      const max = sortedSections[0]?.[1] || 1;
                      return (
                        <div key={section} className="flex items-center gap-3">
                          <span className="text-sm text-white/50 w-36 truncate">{formatSection(section)}</span>
                          <div className="flex-1 h-5 bg-white/[0.03] relative overflow-hidden"><div className="h-full bg-gold/20 transition-all" style={{ width: `${(count / max) * 100}%` }} /></div>
                          <span className="text-sm text-gold/60 w-10 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* VISITORS CRM */}
          {tab === "visitors" && (
            <div className="space-y-4">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
                <h2 className="font-serif text-2xl text-white/80">Visitors CRM</h2>
                <span className="text-sm text-white/30">{visitors.length} contacts</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" />
                  <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by name, email, company..."
                    className="bg-transparent border-white/10 text-white/70 pl-10 rounded-none h-10 text-sm" data-testid="crm-search" />
                </div>
                <Select value={filterSeriousness} onValueChange={setFilterSeriousness}>
                  <SelectTrigger className="w-40 bg-transparent border-white/10 text-white/50 rounded-none h-10 text-sm" data-testid="crm-filter-seriousness">
                    <Filter size={14} className="mr-2" /><SelectValue placeholder="Level" />
                  </SelectTrigger>
                  <SelectContent className="bg-emerald-card border-white/10">
                    <SelectItem value="all">All Levels</SelectItem>
                    {SERIOUSNESS_LEVELS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-36 bg-transparent border-white/10 text-white/50 rounded-none h-10 text-sm" data-testid="crm-filter-status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-emerald-card border-white/10">
                    <SelectItem value="all">All Status</SelectItem>
                    {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Bulk Action Bar */}
              {selectedIds.size > 0 && (
                <div className="flex items-center justify-between px-4 py-3 bg-[#C5A059]/10 border border-[#C5A059]/20" data-testid="bulk-action-bar">
                  <span className="text-sm text-[#C5A059]">{selectedIds.size} selected</span>
                  <div className="flex items-center gap-3">
                    <Button onClick={bulkDelete} disabled={bulkDeleting} variant="ghost" className="text-red-400/70 hover:text-red-400 text-xs gap-2 h-8" data-testid="bulk-delete-btn">
                      <Trash2 size={14} /> {bulkDeleting ? "Deleting..." : "Delete Selected"}
                    </Button>
                    <Button onClick={() => setSelectedIds(new Set())} variant="ghost" className="text-white/40 hover:text-white/60 text-xs h-8" data-testid="bulk-deselect-btn">
                      <X size={14} /> Clear
                    </Button>
                  </div>
                </div>
              )}

              <ScrollArea className="h-[65vh]">
                {/* Select All Header */}
                <div className="flex items-center gap-3 px-4 py-2 mb-1">
                  <button onClick={selectAllVisible} className="text-white/30 hover:text-white/50 transition-colors" data-testid="select-all-btn">
                    {selectedIds.size === visitors.length && visitors.length > 0 ? <CheckSquare size={16} className="text-[#C5A059]" /> : <Square size={16} />}
                  </button>
                  <span className="text-[10px] text-white/20 uppercase tracking-wider">Select All</span>
                </div>
                <div className="space-y-2">
                  {visitors.map((v, i) => (
                    <div key={v.id || i} className={`flex items-center gap-3 p-4 bg-emerald-card border transition-all cursor-pointer ${selectedIds.has(v.id) ? "border-[#C5A059]/30 bg-[#C5A059]/[0.04]" : "border-white/[0.06] hover:border-gold/10"}`} data-testid={`visitor-row-${i}`}>
                      <button onClick={(e) => toggleSelect(v.id, e)} className="flex-shrink-0 text-white/30 hover:text-white/50 transition-colors" data-testid={`visitor-checkbox-${i}`}>
                        {selectedIds.has(v.id) ? <CheckSquare size={16} className="text-[#C5A059]" /> : <Square size={16} />}
                      </button>
                      <div className="flex-1 min-w-0" onClick={() => openVisitorWithEngagement(v)}>
                        <div className="flex items-center gap-3 mb-1">
                          <p className="text-base text-white/70 font-medium truncate">{v.name}</p>
                          <SeriousnessBadge level={v.seriousness} />
                        </div>
                        <p className="text-sm text-white/30 truncate">{v.email} {v.company && `— ${v.company}`}</p>
                      </div>
                      <div className="flex items-center gap-3 ml-4" onClick={() => openVisitorWithEngagement(v)}>
                        <div className="text-right hidden sm:block">
                          <p className="text-xs text-white/20">{v.visit_count || 1} visit{(v.visit_count || 1) > 1 ? "s" : ""}</p>
                          <p className="text-[10px] text-white/15">{new Date(v.registered_at).toLocaleDateString()}</p>
                        </div>
                        <ArrowRight size={14} className="text-white/15" />
                      </div>
                    </div>
                  ))}
                  {visitors.length === 0 && <p className="text-sm text-white/20 text-center py-12">No visitors match your filters.</p>}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* ENQUIRIES */}
          {tab === "inquiries" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-2xl text-white/80">Enquiries</h2>
                <div className="flex items-center gap-3">
                  {selectedInquiryIds.size > 0 && (
                    <Button onClick={bulkDeleteInquiries} variant="ghost" className="text-red-400/70 hover:text-red-400 text-xs gap-2 h-8" data-testid="bulk-delete-inquiries-btn">
                      <Trash2 size={14} /> Delete {selectedInquiryIds.size}
                    </Button>
                  )}
                  <span className="text-sm text-white/30">{inquiries.length} total</span>
                </div>
              </div>
              <ScrollArea className="h-[70vh]">
                <div className="space-y-3">
                  {inquiries.map((inq, i) => (
                    <div key={inq.id || i} className={`p-5 bg-emerald-card border transition-all ${selectedInquiryIds.has(inq.id) ? "border-[#C5A059]/30" : "border-white/[0.06]"}`} data-testid={`inquiry-row-${i}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-3">
                          <button onClick={() => toggleInquirySelect(inq.id)} className="mt-1 text-white/30 hover:text-white/50" data-testid={`inquiry-checkbox-${i}`}>
                            {selectedInquiryIds.has(inq.id) ? <CheckSquare size={16} className="text-[#C5A059]" /> : <Square size={16} />}
                          </button>
                          <div>
                            <p className="text-base text-white/70 font-medium">{inq.name}</p>
                            <p className="text-sm text-white/30">{inq.email} {inq.phone && `| ${inq.phone}`}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-gold/40 bg-gold/[0.06] px-2 py-1 uppercase tracking-wider">{inq.interest_type?.replace(/_/g, " ")}</span>
                          <Button variant="ghost" size="sm" onClick={() => openReplyModal(inq)} className="text-[#C5A059]/40 hover:text-[#C5A059] p-1 h-auto" data-testid={`inquiry-reply-${i}`}>
                            <Send size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditInquiry(inq)} className="text-white/30 hover:text-white/60 p-1 h-auto" data-testid={`inquiry-edit-${i}`}>
                            <Edit size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteInquiry(inq.id)} className="text-red-400/30 hover:text-red-400/60 p-1 h-auto" data-testid={`inquiry-delete-${i}`}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                      <Separator className="bg-white/[0.04] mb-3" />
                      <p className="text-sm text-white/50 leading-relaxed">{inq.message}</p>
                      {(inq.replies || []).length > 0 && (
                        <div className="mt-3 pt-3 border-t border-white/[0.04]">
                          <p className="text-[10px] text-[#C5A059]/40 uppercase tracking-wider mb-2">Replies ({inq.replies.length})</p>
                          {inq.replies.map((r, j) => (
                            <div key={j} className="text-xs text-white/30 mb-1">
                              <span className="text-white/20">{new Date(r.sent_at).toLocaleString()}</span> — {r.subject}
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-[10px] text-white/15 mt-3">{new Date(inq.timestamp).toLocaleString()}</p>
                    </div>
                  ))}
                  {inquiries.length === 0 && <p className="text-sm text-white/20 text-center py-12">No enquiries yet.</p>}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Reply Modal */}
          <Dialog open={replyOpen} onOpenChange={setReplyOpen}>
            <DialogContent className="max-w-lg bg-[#1A1918] border-white/10" data-testid="reply-dialog">
              <DialogHeader><DialogTitle className="font-serif text-xl text-white/80">Reply to {replyTarget?.name}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <p className="text-xs text-white/30">Sending to: {replyTarget?.email}</p>
                <div className="space-y-2">
                  <Label className="text-xs text-white/40 uppercase tracking-wider">Subject</Label>
                  <Input value={replyForm.subject} onChange={(e) => setReplyForm(p => ({ ...p, subject: e.target.value }))}
                    className="bg-transparent border-white/10 text-white/70 rounded-none h-10 text-sm" data-testid="reply-subject" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-white/40 uppercase tracking-wider">Message</Label>
                  <Textarea value={replyForm.body} onChange={(e) => setReplyForm(p => ({ ...p, body: e.target.value }))}
                    rows={6} placeholder="Write your reply..." className="bg-transparent border-white/10 text-white/70 rounded-none text-sm resize-none" data-testid="reply-body" />
                </div>
                <Button onClick={sendReply} disabled={!replyForm.body.trim() || replySending} className="w-full py-4 bg-[#C5A059] hover:bg-[#E8D5A3] text-[#111110] text-xs tracking-[0.15em] uppercase font-medium rounded-none h-auto" data-testid="reply-send-btn">
                  {replySending ? "Sending..." : "Send Reply"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Edit Inquiry Modal */}
          <Dialog open={editInquiryOpen} onOpenChange={setEditInquiryOpen}>
            <DialogContent className="max-w-md bg-[#1A1918] border-white/10" data-testid="edit-inquiry-dialog">
              <DialogHeader><DialogTitle className="font-serif text-xl text-white/80">Edit Enquiry</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-xs text-white/40 uppercase tracking-wider">Name</Label>
                  <Input value={editInquiryForm.name} onChange={(e) => setEditInquiryForm(p => ({ ...p, name: e.target.value }))}
                    className="bg-transparent border-white/10 text-white/70 rounded-none h-10 text-base" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-white/40 uppercase tracking-wider">Email</Label>
                  <Input value={editInquiryForm.email} onChange={(e) => setEditInquiryForm(p => ({ ...p, email: e.target.value }))}
                    className="bg-transparent border-white/10 text-white/70 rounded-none h-10 text-base" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-white/40 uppercase tracking-wider">Message</Label>
                  <Textarea value={editInquiryForm.message} onChange={(e) => setEditInquiryForm(p => ({ ...p, message: e.target.value }))}
                    rows={4} className="bg-transparent border-white/10 text-white/70 rounded-none text-sm resize-none" />
                </div>
                <Button onClick={saveEditInquiry} className="w-full py-4 bg-gold hover:bg-gold-light text-[#111110] text-xs tracking-[0.15em] uppercase font-medium rounded-none h-auto" data-testid="save-inquiry-btn">
                  Save Changes
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* REFERRALS */}
          {tab === "referrals" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-2xl text-white/80">Referral Tracking</h2>
                <Button onClick={() => setAddReferrerOpen(true)} className="bg-[#C5A059]/10 text-[#C5A059] hover:bg-[#C5A059]/20 text-xs gap-2 rounded-none" data-testid="add-referrer-btn">
                  <Plus size={14} /> Add Referrer
                </Button>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="p-4 bg-emerald-card border border-white/[0.06]">
                  <div className="font-serif text-2xl text-white/80 mb-1">{referralStats?.total_referrers || 0}</div>
                  <p className="text-xs text-white/30 uppercase tracking-wider">Active Referrers</p>
                </div>
                <div className="p-4 bg-emerald-card border border-white/[0.06]">
                  <div className="font-serif text-2xl text-white/80 mb-1">{(referralStats?.referrers || []).reduce((a, r) => a + (r.link_clicks || 0), 0)}</div>
                  <p className="text-xs text-white/30 uppercase tracking-wider">Total Link Clicks</p>
                </div>
                <div className="p-4 bg-emerald-card border border-white/[0.06]">
                  <div className="font-serif text-2xl text-white/80 mb-1">{referralStats?.total_referrals || 0}</div>
                  <p className="text-xs text-white/30 uppercase tracking-wider">Registrations</p>
                </div>
              </div>

              <ScrollArea className="h-[55vh]">
                <div className="space-y-3">
                  {(referralStats?.referrers || []).map((ref, i) => (
                    <div key={ref.id || i} className="p-5 bg-emerald-card border border-white/[0.06]" data-testid={`referrer-row-${i}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <p className="text-base text-white/80 font-medium">{ref.name}</p>
                            <span className={`text-[10px] px-2 py-0.5 ${ref.active ? "bg-green-500/10 text-green-400/60" : "bg-red-500/10 text-red-400/60"}`}>
                              {ref.active ? "Active" : "Inactive"}
                            </span>
                            {ref.commission_rate > 0 && (
                              <span className="text-[10px] px-2 py-0.5 bg-[#C5A059]/10 text-[#C5A059]/60">{ref.commission_rate}% commission</span>
                            )}
                          </div>
                          {ref.email && <p className="text-sm text-white/30">{ref.email}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => toggleReferrer(ref.id, ref.active)} className="text-white/30 hover:text-white/60 p-1 h-auto text-xs">
                            {ref.active ? "Deactivate" : "Activate"}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteReferrer(ref.id)} className="text-red-400/30 hover:text-red-400/60 p-1 h-auto">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mb-3 p-3 bg-white/[0.02] border border-white/[0.04]">
                        <span className="text-xs text-white/20">Referral Link:</span>
                        <code className="text-xs text-[#C5A059]/70 bg-[#C5A059]/[0.04] px-2 py-1 flex-1 select-all" data-testid={`referral-link-${i}`}>
                          {getSiteUrl()}?ref={ref.code}
                        </code>
                        <Button variant="ghost" size="sm" className="text-white/30 hover:text-[#C5A059] p-1 h-auto text-xs"
                          onClick={() => { navigator.clipboard.writeText(`${getSiteUrl()}?ref=${ref.code}`); }}>
                          Copy
                        </Button>
                      </div>

                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-white/25">Link Clicks</span>
                          <p className="text-white/70 font-medium">{ref.link_clicks || 0}</p>
                        </div>
                        <div>
                          <span className="text-white/25">Registered</span>
                          <p className="text-white/70 font-medium">{ref.referral_count || 0}</p>
                        </div>
                        <div>
                          <span className="text-white/25">Enquiries</span>
                          <p className="text-[#C5A059]/70 font-medium">{ref.enquiry_count || 0}</p>
                        </div>
                      </div>

                      {/* Funnel */}
                      <div className="mt-3 pt-3 border-t border-white/[0.04]">
                        <p className="text-xs text-white/20 uppercase tracking-wider mb-2">Conversion Funnel</p>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-white/50">{ref.link_clicks || 0} clicks</span>
                          <span className="text-white/15">&rarr;</span>
                          <span className="text-white/50">{ref.referral_count || 0} registered</span>
                          <span className="text-white/10">({ref.link_clicks > 0 ? Math.round((ref.referral_count || 0) / ref.link_clicks * 100) : 0}%)</span>
                          <span className="text-white/15">&rarr;</span>
                          <span className="text-[#C5A059]/60">{ref.enquiry_count || 0} enquiries</span>
                          <span className="text-white/10">({ref.referral_count > 0 ? Math.round((ref.enquiry_count || 0) / ref.referral_count * 100) : 0}%)</span>
                        </div>
                      </div>

                      {(ref.referred_visitors || []).length > 0 && (
                        <div className="mt-3 pt-3 border-t border-white/[0.04]">
                          <p className="text-xs text-white/20 uppercase tracking-wider mb-2">Referred Visitors</p>
                          <div className="space-y-1">
                            {ref.referred_visitors.map((rv, j) => (
                              <div key={j} className="flex items-center justify-between text-xs">
                                <span className="text-white/50">{rv.name} ({rv.email})</span>
                                <span className="text-white/20">{new Date(rv.timestamp).toLocaleDateString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {ref.notes && <p className="mt-3 text-xs text-white/20 italic">{ref.notes}</p>}
                    </div>
                  ))}
                  {(!referralStats?.referrers || referralStats.referrers.length === 0) && (
                    <p className="text-sm text-white/20 text-center py-12">No referrers yet. Add one to start tracking.</p>
                  )}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Add Referrer Dialog */}
          {addReferrerOpen && (
            <Dialog open={addReferrerOpen} onOpenChange={setAddReferrerOpen}>
              <DialogContent className="max-w-md bg-[#1A1918] border-white/10" data-testid="add-referrer-dialog">
                <DialogHeader><DialogTitle className="font-serif text-xl text-white/80">Add New Referrer</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-white/40 uppercase tracking-wider">Name *</Label>
                    <Input value={newReferrer.name} onChange={(e) => setNewReferrer(p => ({ ...p, name: e.target.value }))}
                      placeholder="John Smith" className="bg-transparent border-white/10 text-white/70 rounded-none h-10 text-base" data-testid="referrer-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-white/40 uppercase tracking-wider">Email</Label>
                    <Input value={newReferrer.email} onChange={(e) => setNewReferrer(p => ({ ...p, email: e.target.value }))}
                      placeholder="john@example.com" className="bg-transparent border-white/10 text-white/70 rounded-none h-10 text-base" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-white/40 uppercase tracking-wider">Commission Rate (%)</Label>
                    <Input type="number" value={newReferrer.commission_rate} onChange={(e) => setNewReferrer(p => ({ ...p, commission_rate: e.target.value }))}
                      placeholder="5" className="bg-transparent border-white/10 text-white/70 rounded-none h-10 text-base" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-white/40 uppercase tracking-wider">Notes</Label>
                    <Input value={newReferrer.notes} onChange={(e) => setNewReferrer(p => ({ ...p, notes: e.target.value }))}
                      placeholder="e.g. London jeweller, introduced by..." className="bg-transparent border-white/10 text-white/70 rounded-none h-10 text-sm" />
                  </div>
                  <Button onClick={createReferrer} className="w-full py-4 bg-[#C5A059] hover:bg-[#E8D5A3] text-[#111110] text-xs tracking-[0.15em] uppercase font-medium rounded-none h-auto" data-testid="create-referrer-btn">
                    Create Referrer & Generate Link
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* ANALYTICS */}
          {tab === "analytics" && (
            <div className="space-y-6">
              <h2 className="font-serif text-2xl text-white/80">Analytics</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="p-5 bg-emerald-card border border-white/[0.06]">
                  <h3 className="text-sm text-white/50 uppercase tracking-wider mb-4">Section Views</h3>
                  {sortedSections.length > 0 ? sortedSections.map(([s, c]) => (
                    <div key={s} className="flex justify-between text-sm py-1"><span className="text-white/50">{formatSection(s)}</span><span className="text-gold/60">{c}</span></div>
                  )) : <p className="text-sm text-white/20">No data yet.</p>}
                </div>
                <div className="p-5 bg-emerald-card border border-white/[0.06]">
                  <h3 className="text-sm text-white/50 uppercase tracking-wider mb-4">Scroll Depth</h3>
                  {(analytics?.scroll_depths || []).slice(0, 10).map((ev, i) => {
                    const visitor = visitors.find(v => v.id === ev.visitor_id);
                    const label = visitor ? visitor.name : (ev.visitor_id?.slice(0, 8) + "...");
                    return (
                      <div key={i} className="flex justify-between text-sm py-1"><span className="text-white/40">{label}</span><span className="text-gold/60">{ev.data?.depth || 0}%</span></div>
                    );
                  })}
                </div>
              </div>
              <div className="p-5 bg-emerald-card border border-white/[0.06]">
                <h3 className="text-sm text-white/50 uppercase tracking-wider mb-4">Live Event Feed</h3>
                <ScrollArea className="h-64">
                  <div className="space-y-1">
                    {(analytics?.recent_events || []).map((ev, i) => {
                      const visitor = visitors.find(v => v.id === ev.visitor_id);
                      const label = visitor ? visitor.name : (ev.visitor_id?.slice(0, 8) || "anon");
                      return (
                        <div key={i} className="flex items-center gap-3 text-xs py-1.5 border-b border-white/[0.03]">
                          <span className="text-white/30 w-24 truncate">{label}</span>
                          <span className={`px-1.5 py-0.5 ${ev.event_type === "click" ? "text-blue-400/60 bg-blue-400/10" : ev.event_type === "section_view" ? "text-green-400/60 bg-green-400/10" : "text-white/40 bg-white/[0.05]"}`}>{ev.event_type?.replace(/_/g, " ")}</span>
                          {ev.section && <span className="text-white/30">{formatSection(ev.section)}</span>}
                          <span className="text-white/15 ml-auto">{new Date(ev.timestamp).toLocaleTimeString()}</span>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Visitor Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl bg-[#1A1918] border-white/10 max-h-[90vh] overflow-y-auto" data-testid="visitor-detail-dialog">
          {selectedVisitor && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-white/80 flex items-center gap-3">
                  {selectedVisitor.name}
                  <SeriousnessBadge level={selectedVisitor.seriousness} />
                </DialogTitle>
              </DialogHeader>

              {/* Detail Tabs */}
              <div className="flex gap-1 mt-2 border-b border-white/[0.06] pb-0">
                {[
                  { id: "info", label: "Details", icon: Users },
                  { id: "engagement", label: "Engagement", icon: Activity },
                ].map(t => (
                  <button key={t.id} onClick={() => { setDetailTab(t.id); if (t.id === "engagement" && !engagementData) fetchEngagement(selectedVisitor.id); }}
                    className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider transition-colors border-b-2 -mb-[1px] ${detailTab === t.id ? "border-[#C5A059] text-[#C5A059]" : "border-transparent text-white/30 hover:text-white/50"}`}
                    data-testid={`detail-tab-${t.id}`}>
                    <t.icon size={14} />{t.label}
                  </button>
                ))}
              </div>

              {/* INFO TAB */}
              {detailTab === "info" && (
                <div className="space-y-5 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2"><Mail size={14} className="text-gold/40" /><span className="text-sm text-white/60">{selectedVisitor.email}</span></div>
                    <div className="flex items-center gap-2"><Phone size={14} className="text-gold/40" /><span className="text-sm text-white/60">{selectedVisitor.phone || "Not provided"}</span></div>
                    <div className="flex items-center gap-2"><Building size={14} className="text-gold/40" /><span className="text-sm text-white/60">{selectedVisitor.company || "Not provided"}</span></div>
                    <div className="flex items-center gap-2"><Calendar size={14} className="text-gold/40" /><span className="text-sm text-white/60">{new Date(selectedVisitor.registered_at).toLocaleDateString()}</span></div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {(selectedVisitor.tags || []).map((tag, i) => (
                      <span key={i} className="text-[10px] px-2 py-1 bg-white/[0.05] text-white/40 border border-white/[0.06]">{tag}</span>
                    ))}
                    {selectedVisitor.agreed_terms && <span className="text-[10px] px-2 py-1 bg-green-500/10 text-green-400/60">T&Cs Agreed</span>}
                    {selectedVisitor.agreed_nda && <span className="text-[10px] px-2 py-1 bg-green-500/10 text-green-400/60">NDA Signed</span>}
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <Select value={selectedVisitor.seriousness || "new_lead"} onValueChange={(v) => updateVisitor(selectedVisitor.id, { seriousness: v })}>
                      <SelectTrigger className="bg-transparent border-white/10 text-white/50 rounded-none h-9 text-sm w-44" data-testid="visitor-seriousness-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-emerald-card border-white/10">
                        {SERIOUSNESS_LEVELS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={selectedVisitor.status || "active"} onValueChange={(v) => updateVisitor(selectedVisitor.id, { status: v })}>
                      <SelectTrigger className="bg-transparent border-white/10 text-white/50 rounded-none h-9 text-sm w-40"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-emerald-card border-white/10">
                        {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" onClick={() => openEdit(selectedVisitor)} className="text-white/30 hover:text-gold text-xs h-9" data-testid="visitor-edit-btn"><Edit size={14} className="mr-1" /> Edit</Button>
                    <Button variant="ghost" onClick={() => sendMagicLink(selectedVisitor.id)} disabled={magicLinkSending} className="text-[#C5A059]/50 hover:text-[#C5A059] text-xs h-9 gap-1" data-testid="send-magic-link-btn">
                      <Send size={14} /> {magicLinkSending ? "Sending..." : "Send Magic Link"}
                    </Button>
                  </div>

                  <Separator className="bg-white/[0.06]" />

                  {/* Visit History */}
                  <div>
                    <h4 className="text-xs text-white/40 uppercase tracking-wider mb-3 flex items-center gap-2"><Eye size={14} /> Visit History ({selectedVisitor.visit_count || 1} visits)</h4>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {(selectedVisitor.visits || []).map((visit, i) => (
                        <div key={i} className="flex items-center gap-3 text-sm">
                          <span className="w-2 h-2 bg-gold/30 rounded-full" />
                          <span className="text-white/40">{new Date(visit.timestamp).toLocaleString()}</span>
                          <span className="text-white/20">{visit.type?.replace(/_/g, " ")}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Enquiries from this visitor */}
                  {(selectedVisitor.inquiries || []).length > 0 && (
                    <div>
                      <h4 className="text-xs text-white/40 uppercase tracking-wider mb-3 flex items-center gap-2"><MessageSquare size={14} /> Enquiries</h4>
                      <div className="space-y-2">
                        {selectedVisitor.inquiries.map((inq, i) => (
                          <div key={i} className="p-3 bg-white/[0.02] border border-white/[0.04]">
                            <div className="flex justify-between mb-1">
                              <span className="text-xs text-gold/50">{inq.type?.replace(/_/g, " ")}</span>
                              <span className="text-[10px] text-white/20">{new Date(inq.timestamp).toLocaleDateString()}</span>
                            </div>
                            <p className="text-sm text-white/40">{inq.message}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Separator className="bg-white/[0.06]" />

                  {/* Notes */}
                  <div>
                    <h4 className="text-xs text-white/40 uppercase tracking-wider mb-3 flex items-center gap-2"><StickyNote size={14} /> Notes</h4>
                    <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                      {(selectedVisitor.notes || []).map((note, i) => (
                        <div key={i} className="p-3 bg-white/[0.02] border border-white/[0.04] group">
                          <div className="flex justify-between mb-1">
                            <span className="text-[10px] text-white/20">{new Date(note.created_at).toLocaleString()} — {note.author}</span>
                            <button onClick={() => deleteNote(note.id)} className="text-red-400/0 group-hover:text-red-400/40 transition-colors"><X size={12} /></button>
                          </div>
                          <p className="text-sm text-white/50">{note.content}</p>
                        </div>
                      ))}
                      {(!selectedVisitor.notes || selectedVisitor.notes.length === 0) && <p className="text-xs text-white/15">No notes yet.</p>}
                    </div>
                    <div className="flex gap-2">
                      <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note..." rows={2}
                        className="bg-transparent border-white/10 text-white/70 rounded-none text-sm resize-none flex-1" data-testid="visitor-note-input" />
                      <Button onClick={addNote} disabled={!noteText.trim()} className="bg-gold/10 text-gold hover:bg-gold/20 rounded-none h-auto px-4" data-testid="visitor-add-note-btn">
                        <Plus size={16} />
                      </Button>
                    </div>
                  </div>

                  <Separator className="bg-white/[0.06]" />

                  <Button variant="ghost" onClick={() => deleteVisitor(selectedVisitor.id)} className="text-red-400/40 hover:text-red-400 text-xs gap-2" data-testid="visitor-delete-btn">
                    <Trash2 size={14} /> Delete Visitor
                  </Button>
                </div>
              )}

              {/* ENGAGEMENT TAB */}
              {detailTab === "engagement" && (
                <div className="space-y-5 mt-4">
                  {engagementLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-sm text-white/30 animate-pulse">Loading engagement data...</div>
                    </div>
                  ) : engagementData ? (
                    <>
                      {/* Engagement Score + Summary */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <div className="p-4 bg-emerald-card border border-white/[0.06] text-center">
                          <div className="relative w-16 h-16 mx-auto mb-2">
                            <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                              <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="3" />
                              <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none"
                                stroke={engagementData.engagement_score > 60 ? "#22c55e" : engagementData.engagement_score > 30 ? "#C5A059" : "#ef4444"}
                                strokeWidth="3" strokeDasharray={`${engagementData.engagement_score}, 100`} strokeLinecap="round" />
                            </svg>
                            <span className="absolute inset-0 flex items-center justify-center text-sm font-medium text-white/80">{Math.round(engagementData.engagement_score)}</span>
                          </div>
                          <p className="text-[10px] text-white/30 uppercase tracking-wider">Score</p>
                        </div>
                        <div className="p-4 bg-emerald-card border border-white/[0.06]">
                          <div className="font-serif text-2xl text-white/80 mb-1">{engagementData.total_time_seconds > 60 ? `${Math.round(engagementData.total_time_seconds / 60)}m` : `${engagementData.total_time_seconds}s`}</div>
                          <p className="text-[10px] text-white/30 uppercase tracking-wider">Total Time</p>
                        </div>
                        <div className="p-4 bg-emerald-card border border-white/[0.06]">
                          <div className="font-serif text-2xl text-white/80 mb-1">{engagementData.sections_viewed}</div>
                          <p className="text-[10px] text-white/30 uppercase tracking-wider">Sections</p>
                        </div>
                        <div className="p-4 bg-emerald-card border border-white/[0.06]">
                          <div className="font-serif text-2xl text-white/80 mb-1">{engagementData.max_scroll_depth}%</div>
                          <p className="text-[10px] text-white/30 uppercase tracking-wider">Scroll Depth</p>
                        </div>
                      </div>

                      {/* Section Breakdown */}
                      {Object.keys(engagementData.section_breakdown).length > 0 && (
                        <div className="p-5 bg-emerald-card border border-white/[0.06]">
                          <h4 className="text-xs text-white/40 uppercase tracking-wider mb-4">Section Breakdown — Reading Behavior</h4>
                          <div className="space-y-3">
                            {Object.entries(engagementData.section_breakdown)
                              .sort((a, b) => b[1].total_time - a[1].total_time)
                              .map(([section, data]) => {
                                const maxTime = Math.max(...Object.values(engagementData.section_breakdown).map(d => d.total_time), 1);
                                return (
                                  <div key={section} className="space-y-1">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm text-white/60">{formatSection(section)}</span>
                                        <span className={`text-[10px] px-2 py-0.5 ${behaviorColor(data.behavior)}`}>{behaviorLabel(data.behavior)}</span>
                                      </div>
                                      <span className="text-xs text-white/30">{data.total_time}s ({data.views} views, avg {data.avg_time}s)</span>
                                    </div>
                                    <div className="h-2 bg-white/[0.03] overflow-hidden">
                                      <div className={`h-full transition-all ${data.behavior === "deep_reading" ? "bg-green-500/40" : data.behavior === "reading" ? "bg-emerald-500/40" : data.behavior === "skimming" ? "bg-amber-500/40" : "bg-red-500/40"}`}
                                        style={{ width: `${(data.total_time / maxTime) * 100}%` }} />
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}

                      {/* Interaction History */}
                      {engagementData.interactions.length > 0 && (
                        <div className="p-5 bg-emerald-card border border-white/[0.06]">
                          <h4 className="text-xs text-white/40 uppercase tracking-wider mb-4">Interaction History ({engagementData.total_interactions} total)</h4>
                          <ScrollArea className="h-48">
                            <div className="space-y-1">
                              {engagementData.interactions.slice().reverse().map((interaction, i) => (
                                <div key={i} className="flex items-center gap-3 text-xs py-1.5 border-b border-white/[0.03]">
                                  <span className={`px-1.5 py-0.5 font-mono ${interaction.type === "document_view" ? "text-blue-400/60 bg-blue-400/10" : interaction.type === "gallery_view" ? "text-purple-400/60 bg-purple-400/10" : interaction.type === "video_play" ? "text-pink-400/60 bg-pink-400/10" : "text-white/40 bg-white/[0.05]"}`}>
                                    {interactionIcon(interaction.type)}
                                  </span>
                                  <span className="text-white/50 flex-1 truncate">
                                    {interaction.item || interaction.element || "—"}
                                    {interaction.section && <span className="text-white/20 ml-2">in {formatSection(interaction.section)}</span>}
                                  </span>
                                  {interaction.timestamp && <span className="text-white/15 flex-shrink-0">{new Date(interaction.timestamp).toLocaleString()}</span>}
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </div>
                      )}

                      {/* Sessions */}
                      {engagementData.sessions.length > 0 && (
                        <div className="p-5 bg-emerald-card border border-white/[0.06]">
                          <h4 className="text-xs text-white/40 uppercase tracking-wider mb-4">Sessions</h4>
                          <div className="space-y-2">
                            {engagementData.sessions.map((s, i) => (
                              <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-white/[0.03]">
                                <div className="flex items-center gap-2">
                                  <Zap size={12} className="text-gold/40" />
                                  <span className="text-white/40">{s.type === "page_load" ? "Page Load" : `Session (${s.duration}s)`}</span>
                                </div>
                                {s.timestamp && <span className="text-[10px] text-white/20">{new Date(s.timestamp).toLocaleString()}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {engagementData.total_events === 0 && (
                        <div className="text-center py-12">
                          <Activity size={24} strokeWidth={1} className="text-white/10 mx-auto mb-3" />
                          <p className="text-sm text-white/20">No engagement data recorded yet for this visitor.</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <Activity size={24} strokeWidth={1} className="text-white/10 mx-auto mb-3" />
                      <p className="text-sm text-white/20">Click to load engagement data.</p>
                      <Button onClick={() => fetchEngagement(selectedVisitor.id)} variant="ghost" className="text-[#C5A059]/50 hover:text-[#C5A059] text-xs mt-3" data-testid="load-engagement-btn">
                        Load Engagement Data
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md bg-[#1A1918] border-white/10" data-testid="visitor-edit-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl text-white/80">Edit Visitor</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-xs text-white/40 uppercase tracking-wider">Name</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))} className="bg-transparent border-white/10 text-white/70 rounded-none h-10 text-base" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-white/40 uppercase tracking-wider">Phone</Label>
              <Input value={editForm.phone} onChange={(e) => setEditForm(p => ({ ...p, phone: e.target.value }))} className="bg-transparent border-white/10 text-white/70 rounded-none h-10 text-base" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-white/40 uppercase tracking-wider">Company</Label>
              <Input value={editForm.company} onChange={(e) => setEditForm(p => ({ ...p, company: e.target.value }))} className="bg-transparent border-white/10 text-white/70 rounded-none h-10 text-base" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-white/40 uppercase tracking-wider">Tags (comma-separated)</Label>
              <Input value={editForm.tags} onChange={(e) => setEditForm(p => ({ ...p, tags: e.target.value }))} placeholder="collector, high-net-worth, museum" className="bg-transparent border-white/10 text-white/70 rounded-none h-10 text-sm" />
            </div>
            <Button onClick={saveEdit} className="w-full py-4 bg-gold hover:bg-gold-light text-[#111110] text-xs tracking-[0.15em] uppercase font-medium rounded-none h-auto" data-testid="visitor-save-btn">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
